import { useState, useCallback } from 'react'
import { useAuth } from '../providers/auth.provider'

type CheckoutType = 'progress_saving' | 'unlock_all' | 'unit'

type UseCheckout = {
  startCheckout: (type: CheckoutType, unitSlug?: string) => Promise<void>
  loading: boolean
  error: string | null
}

export const useCheckout = (): UseCheckout => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const startCheckout = useCallback(async (type: CheckoutType, unitSlug?: string): Promise<void> => {
    if (!user) {
      return
    }

    if (type === 'unit' && !unitSlug) {
      setError('A unit slug is required when checking out a unit.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const token = await user.getIdToken()

      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type, unitSlug, returnUrl: window.location.href }),
      })

      if (!response.ok) {
        setError('Checkout failed. Please try again.')
        setLoading(false)
        return
      }

      const data: { url: string } = await response.json()

      if (!data.url) {
        setError('Checkout session URL missing')
        setLoading(false)
        return
      }

      window.location.href = data.url
    } catch {
      setError('A network error occurred. Please try again.')
      setLoading(false)
    }
  }, [user])

  return { startCheckout, loading, error }
}
